import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";
import { INSIGHTS } from "../lib/insights";

const ArticlePage = () => {
    const { slug } = useParams();
    const article = INSIGHTS.find((a) => a.slug === slug);
    if (!article) return <Navigate to="/insights" replace />;

    const others = INSIGHTS.filter((a) => a.slug !== slug);

    return (
        <div className="min-h-screen bg-ink text-white">
            <Seo title={`${article.title} | BuildEase Insights`} description={article.excerpt} />
            <Navbar />
            <main>
                <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                    <div className="relative z-10 mx-auto max-w-[900px] px-6 md:px-10 lg:px-16">
                        <motion.div variants={fadeUp} initial="hidden" animate="show">
                            <Link
                                to="/insights"
                                data-testid="article-back-link"
                                className="font-mono text-[13px] tracking-[0.3em] text-mist transition-colors duration-300 hover:text-accent"
                            >
                                ← BUILDEASE INSIGHTS
                            </Link>
                            <h1
                                data-testid="article-title"
                                className="mt-8 font-display text-3xl font-semibold leading-[1.12] text-white md:text-5xl"
                            >
                                {article.title}
                            </h1>
                            <p className="mt-6 flex items-center gap-3 font-mono text-[13px] tracking-[0.22em] text-cad">
                                <span className="text-accent">{article.readTime}</span>
                                · {article.date}
                            </p>
                        </motion.div>
                    </div>
                </section>

                <section className="relative py-12 lg:py-16">
                    <div className="mx-auto max-w-[900px] px-6 md:px-10 lg:px-16">
                        <motion.figure
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={viewportOnce}
                            className="overflow-hidden border border-white/12"
                        >
                            <img
                                src={article.image}
                                alt={article.alt}
                                className="aspect-[16/8] w-full object-cover"
                            />
                        </motion.figure>

                        <motion.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={viewportOnce}
                            className="mt-12"
                        >
                            <motion.p
                                variants={fadeUp}
                                className="border-l-2 border-accent pl-5 text-[17px] leading-relaxed text-white/90"
                            >
                                {article.excerpt}
                            </motion.p>
                            {article.sections.map((s) => (
                                <motion.div key={s.h} variants={fadeUp} className="mt-10">
                                    <h2 className="font-display text-2xl font-semibold leading-snug text-white">
                                        {s.h}
                                    </h2>
                                    {s.p.map((para, i) => (
                                        <p
                                            key={i}
                                            className="mt-4 text-[16px] leading-relaxed text-mist"
                                        >
                                            {para}
                                        </p>
                                    ))}
                                </motion.div>
                            ))}

                            <motion.div
                                variants={fadeUp}
                                data-testid="article-takeaway"
                                className="mt-12 border border-accent/40 bg-accent/[0.05] px-6 py-6"
                            >
                                <p className="font-mono text-[13px] tracking-[0.3em] text-accent">
                                    THE TAKEAWAY
                                </p>
                                <p className="mt-3 text-[16px] leading-relaxed text-white/90">
                                    {article.takeaway}
                                </p>
                            </motion.div>

                            <motion.p
                                variants={fadeUp}
                                className="mt-10 border-t border-white/10 pt-6 font-mono text-[13px] leading-relaxed tracking-[0.14em] text-cad"
                            >
                                THIS ARTICLE IS GENERAL GUIDANCE, NOT
                                PROFESSIONAL ADVICE. BUILD DECISIONS SHOULD BE
                                REVIEWED WITH APPROPRIATE QUALIFIED
                                PROFESSIONALS.
                            </motion.p>
                        </motion.div>

                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={viewportOnce}
                            className="mt-14 grid gap-4 sm:grid-cols-2"
                        >
                            {others.map((o) => (
                                <Link
                                    key={o.slug}
                                    to={`/insights/${o.slug}`}
                                    data-testid={`article-next-${o.slug}`}
                                    className="group border border-white/12 bg-surface/80 px-6 py-5 transition-colors duration-300 hover:border-accent/50"
                                >
                                    <p className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                        READ NEXT
                                    </p>
                                    <p className="mt-3 flex items-center justify-between gap-3 font-display text-[16px] font-semibold leading-snug text-white">
                                        {o.title}
                                        <ArrowUpRight className="h-4 w-4 shrink-0 text-accent" />
                                    </p>
                                </Link>
                            ))}
                        </motion.div>

                        <div className="mt-14 text-center">
                            <Link
                                to="/join-the-pilot"
                                data-testid="article-get-started"
                                className="inline-flex items-center gap-2 bg-accent px-7 py-4 text-[16px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                            >
                                Get Started with BuildEase
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default ArticlePage;
